import React, { useState } from 'react';
import { Link, router } from '@inertiajs/react';
import Layout from '../../../components/Layout.jsx';

function StatusBadge({ status }) {
  if (!status) return null;
  const palette = {
    Live:     'bg-emerald-50 text-emerald-700 border-emerald-200',
    Draft:    'bg-amber-50 text-amber-700 border-amber-200',
    Archived: 'bg-gray-100 text-gray-600 border-gray-200',
  }[status] || 'bg-gray-100 text-gray-700 border-gray-200';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${palette}`}>
      {status}
    </span>
  );
}

function RoleBadge({ role }) {
  if (!role) return null;
  const palette = role === 'Administrator'
    ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
    : 'bg-blue-50 text-blue-700 border-blue-200';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${palette}`}>
      {role}
    </span>
  );
}

export default function SuperadminAdminCoursesIndex({
  admin_courses = [],
  filters = {},
  facets = {},
  total_count = 0,
}) {
  const [query, setQuery] = useState(filters.q || '');
  const [status, setStatus] = useState(filters.status || '');
  const [role, setRole] = useState(filters.role || '');

  const applyFilters = (overrides = {}) => {
    router.get('/superadmin/admin_courses', {
      q: overrides.q ?? query,
      status: overrides.status ?? status,
      role: overrides.role ?? role,
    }, { preserveState: true, preserveScroll: true });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    applyFilters();
  };

  const handleClear = () => {
    setQuery('');
    setStatus('');
    setRole('');
    router.get('/superadmin/admin_courses', {}, { preserveState: true, preserveScroll: true });
  };

  const showing = admin_courses.length;
  const hasFilter = (filters.q || filters.status || filters.role || '').toString().trim().length > 0;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6 sm:p-8 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 mb-2 rounded-full bg-purple-50 text-purple-700 border border-purple-200 text-xs font-medium">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              Superadmin
            </div>
            <h1 className="text-3xl font-bold tracking-tight text-gray-900">Courses</h1>
          </div>
          <Link
            href="/superadmin/admin_courses/new"
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm transition"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
            Add course
          </Link>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <form onSubmit={handleSearch} className="flex flex-col lg:flex-row gap-3">
            <div className="relative flex-1">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
                </svg>
              </span>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name, role, or status…"
                className="w-full pl-10 pr-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>
            <select
              value={status}
              onChange={(e) => { setStatus(e.target.value); applyFilters({ status: e.target.value }); }}
              className="rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              <option value="">All statuses</option>
              {(facets.statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select
              value={role}
              onChange={(e) => { setRole(e.target.value); applyFilters({ role: e.target.value }); }}
              className="rounded-lg border border-gray-200 px-3 py-2.5 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              <option value="">All roles</option>
              {(facets.roles || []).map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <div className="flex gap-2">
              <button
                type="submit"
                className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm transition cursor-pointer"
              >
                Search
              </button>
              {hasFilter && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold rounded-lg text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 transition cursor-pointer"
                >
                  Clear
                </button>
              )}
            </div>
          </form>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-indigo-50 text-indigo-600 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900">
              {hasFilter ? 'Matching courses' : 'All courses'}
            </h2>
            <span className="ml-auto text-sm text-gray-400">
              {showing} of {total_count}
              {showing === 500 && ' (showing first 500)'}
            </span>
          </div>

          {showing === 0 ? (
            <div className="text-center py-12">
              <div className="mx-auto w-12 h-12 bg-gray-50 text-gray-400 rounded-2xl flex items-center justify-center mb-3">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
                </svg>
              </div>
              <p className="text-gray-600">
                {hasFilter ? 'No courses match those filters.' : 'No courses yet. Click "Add course" to create one.'}
              </p>
              {hasFilter && (
                <button onClick={handleClear} className="mt-3 text-sm font-semibold text-indigo-600 hover:text-indigo-700 cursor-pointer">
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {admin_courses.map((course) => (
                <div key={course.id} className="flex flex-col sm:flex-row sm:items-start sm:justify-between py-4 gap-3 first:pt-0 last:pb-0">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-gray-900 truncate">{course.name}</p>
                      <StatusBadge status={course.status} />
                      <RoleBadge role={course.role} />
                      {course.course_type && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
                          {course.course_type}
                        </span>
                      )}
                    </div>
                    <div className="mt-1.5 flex items-center gap-x-3 gap-y-1 flex-wrap text-xs text-gray-500">
                      {course.ceu_credit_hours != null && (
                        <span><span className="font-semibold text-gray-700">{course.ceu_credit_hours}</span> CEU hr</span>
                      )}
                      {course.course_fee != null && (
                        <span>Fee: <span className="font-semibold text-gray-700">${course.course_fee}</span></span>
                      )}
                      {(course.license_types || []).map((lt) => (
                        <span key={lt} className="inline-flex items-center px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-[10px] font-bold font-mono">{lt}</span>
                      ))}
                      {course.updated_at && (
                        <span>Updated {course.updated_at}</span>
                      )}
                    </div>
                    {(course.core_of_knowledge || []).length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {course.core_of_knowledge.map((c) => (
                          <span key={c} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-50 text-purple-700 border border-purple-200">
                            {c}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="shrink-0 flex gap-2">
                    <Link
                      href={`/superadmin/admin_courses/${course.id}`}
                      className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 transition"
                    >
                      View
                    </Link>
                    <Link
                      href={`/superadmin/admin_courses/${course.id}/edit`}
                      className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg text-gray-700 bg-white hover:bg-gray-50 border border-gray-200 transition"
                    >
                      Edit
                    </Link>
                    {course.sharepoint_link && (
                      <a
                        href={course.sharepoint_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 transition"
                      >
                        SharePoint
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
